import { useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, X, Sparkles, Download, ArrowRight, Shirt,
  Briefcase, PartyPopper, Dumbbell, Clock, Umbrella, Crown, Shield,
  Stethoscope, Zap, Gem, ChevronDown, Camera
} from "lucide-react";
import axios from "axios";
import { SEO } from "./components/shared/SEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const OUTFIT_PRESETS = [
  { id: "business_suit",   name: "Business Suit",     icon: Briefcase },
  { id: "smart_casual",    name: "Smart Casual",      icon: Shirt },
  { id: "evening_formal",  name: "Evening Formal",    icon: PartyPopper },
  { id: "streetwear",      name: "Streetwear",        icon: Zap },
  { id: "athletic",        name: "Athletic Wear",     icon: Dumbbell },
  { id: "vintage",         name: "Vintage Classic",   icon: Clock },
  { id: "resort_beach",    name: "Beach / Resort",    icon: Umbrella },
  { id: "luxury_designer", name: "Luxury Designer",   icon: Crown },
  { id: "military_uniform",name: "Military Uniform",  icon: Shield },
  { id: "doctor",          name: "Doctor",            icon: Stethoscope },
  { id: "superhero",       name: "Superhero",         icon: Zap },
  { id: "leather_jacket",  name: "Leather Jacket",    icon: Gem },
];

const OutfitChangePage = ({ user, onUserUpdate, onNavigate }) => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedBase64, setUploadedBase64] = useState(null);
  const [selectedOutfit, setSelectedOutfit] = useState(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const compressImage = useCallback((dataUrl) => {
    return new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const MAX_DIM = 1200;
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width > height) { height = Math.round((height * MAX_DIM) / width); width = MAX_DIM; }
          else { width = Math.round((width * MAX_DIM) / height); height = MAX_DIM; }
        }
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = dataUrl;
    });
  }, []);

  const handleFileSelect = useCallback((file) => {
    if (!file || !file.type.startsWith("image/")) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const compressed = await compressImage(e.target.result);
      setUploadedImage(compressed);
      setUploadedBase64(compressed.split(",")[1]);
      setResult(null);
      setError("");
    };
    reader.readAsDataURL(file);
  }, [compressImage]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  }, [handleFileSelect]);

  const handleGenerate = async () => {
    const outfitType = showCustom ? "custom" : selectedOutfit;
    if (!outfitType || !uploadedBase64) return;
    if (showCustom && !customPrompt.trim()) return;

    if ((user?.credits || 0) < 1) {
      setShowUpsell(true);
      return;
    }

    setGenerating(true);
    setResult(null);
    setError("");

    try {
      const res = await axios.post(`${API}/outfit-change`, {
        user_id: user.id,
        image_base64: uploadedBase64,
        outfit_type: outfitType,
        custom_prompt: showCustom ? customPrompt : undefined
      });

      const jobId = res.data.job_id;

      pollRef.current = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/generate/status/${jobId}`);
          if (statusRes.data.status === "completed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setResult(statusRes.data);
            if (statusRes.data.credits_remaining !== undefined) {
              onUserUpdate({ ...user, credits: statusRes.data.credits_remaining });
            }
            setGenerating(false);
          } else if (statusRes.data.status === "error") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setError(statusRes.data.error || "Generation failed");
            setGenerating(false);
          }
        } catch {
          // continue polling
        }
      }, 3000);
    } catch (err) {
      if (err.response?.status === 402) {
        setShowUpsell(true);
      } else {
        setError(err.response?.data?.detail || "Outfit change failed");
      }
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!result?.portrait_id) return;
    try {
      const res = await axios.get(`${API}/portraits/download-hd/${result.portrait_id}?user_id=${user.id}`);
      const link = document.createElement("a");
      link.href = `data:image/png;base64,${res.data.image_base64}`;
      link.download = `starframe-outfit-${selectedOutfit || "custom"}.png`;
      link.click();
      if (res.data.credits_remaining !== undefined) {
        onUserUpdate({ ...user, credits: res.data.credits_remaining });
      }
    } catch {
      if (result?.image_base64) {
        const link = document.createElement("a");
        link.href = `data:image/png;base64,${result.image_base64}`;
        link.download = `starframe-outfit-${selectedOutfit || "custom"}.png`;
        link.click();
      }
    }
  };

  const isReady = uploadedBase64 && (selectedOutfit || (showCustom && customPrompt.trim())) && !generating;

  // ─── AUTH GUARD ───
  if (!user) {
    onNavigate("/start");
    return null;
  }

  // ─── RESULT VIEW ───
  if (result) {
    return (
      <motion.div className="min-h-screen bg-[#050505] pt-20 px-4 pb-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="max-w-lg mx-auto space-y-4">
          <h2 className="text-white font-bold text-lg text-center">Before & After</h2>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-xl overflow-hidden border border-white/10">
              <p className="text-[10px] text-white/40 text-center py-1 bg-white/5">Original</p>
              <img src={uploadedImage} alt="Original" className="w-full aspect-[3/4] object-cover" />
            </div>
            <div className="rounded-xl overflow-hidden border border-[#D4AF37]/30">
              <p className="text-[10px] text-[#D4AF37] text-center py-1 bg-[#D4AF37]/5">New Outfit</p>
              <img src={`data:image/png;base64,${result.image_base64}`} alt="Result" className="w-full aspect-[3/4] object-cover" />
            </div>
          </div>
          <button data-testid="outfit-download-btn" onClick={handleDownload}
            className="w-full bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 border border-[#D4AF37]/30">
            <Download className="w-4 h-4" /> Download HD (1 Credit)
          </button>
          <button onClick={() => setResult(null)}
            className="w-full bg-white/5 hover:bg-white/10 text-white/60 py-2.5 rounded-xl transition-colors text-sm flex items-center justify-center gap-2">
            <ArrowRight className="w-4 h-4" /> Try Another Outfit
          </button>
        </div>
      </motion.div>
    );
  }

  // ─── GENERATING VIEW ───
  if (generating) {
    return (
      <motion.div className="min-h-screen bg-[#050505] pt-20 flex items-center justify-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <div className="absolute inset-0 border-2 border-[#D4AF37]/20 rounded-full animate-spin" style={{ animationDuration: "3s" }} />
            <div className="absolute inset-2 border-2 border-[#D4AF37]/40 rounded-full animate-spin" style={{ animationDuration: "2s", animationDirection: "reverse" }} />
            <div className="absolute inset-4 border-2 border-[#D4AF37]/60 rounded-full animate-spin" style={{ animationDuration: "1.5s" }} />
            <Shirt className="absolute inset-0 m-auto w-6 h-6 text-[#D4AF37]" />
          </div>
          <p className="text-white/60 text-sm">AI is changing your outfit...</p>
          <p className="text-white/30 text-xs mt-1">This may take up to 60 seconds</p>
        </div>
      </motion.div>
    );
  }

  // ─── MAIN FORM VIEW ───
  return (
    <motion.div className="min-h-screen bg-[#050505] pt-20 pb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <SEO title="AI Outfit Change" path="/outfit" description="Change your outfit with AI. 12 presets: business suit, streetwear, evening formal, luxury designer, superhero & more. Or describe any custom outfit. Face-preserving technology." />
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center py-2 mb-2">
          <h1 className="text-lg md:text-2xl font-bold text-white" data-testid="outfit-page-title">Change Your Outfit</h1>
          <p className="text-white/60 text-xs">
            <span className="text-[#D4AF37]">{user?.credits || 0} credits</span> | 1 credit per use
          </p>
        </div>

        {/* Upload — compact on mobile */}
        <div
          data-testid="outfit-upload-zone"
          className={`border-2 border-dashed rounded-xl text-center transition-all mb-3 ${
            uploadedImage ? "p-2" : "p-3"
          } ${dragOver ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/20"}`}
          onDrop={handleDrop}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
        >
          {uploadedImage ? (
            <div className="relative inline-block">
              <img src={uploadedImage} alt="Uploaded" className="h-16 md:h-24 mx-auto rounded-lg" />
              <button data-testid="outfit-remove-upload"
                onClick={() => { setUploadedImage(null); setUploadedBase64(null); setResult(null); }}
                className="absolute -top-1 -right-1 p-1 bg-black/80 rounded-full hover:bg-red-600">
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ) : (
            <label className="flex items-center justify-center gap-3 cursor-pointer py-1">
              <input type="file" accept="image/*" onChange={e => handleFileSelect(e.target.files[0])} className="hidden" data-testid="outfit-file-input" />
              <Camera className="w-5 h-5 text-white/40" />
              <span className="text-white/60 text-sm">Upload your photo</span>
            </label>
          )}
        </div>

        {/* Outfit Grid — 3 cols mobile, 4 cols tablet+ */}
        <h3 className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-2">Choose Outfit</h3>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 sm:gap-2 mb-3">
          {OUTFIT_PRESETS.map(preset => {
            const Icon = preset.icon;
            const active = selectedOutfit === preset.id && !showCustom;
            return (
              <button
                key={preset.id}
                data-testid={`outfit-${preset.id}`}
                onClick={() => { setSelectedOutfit(preset.id); setShowCustom(false); }}
                className={`p-2 rounded-xl text-center transition-all ${
                  active
                    ? "bg-[#D4AF37]/15 border-2 border-[#D4AF37]"
                    : "bg-white/5 border-2 border-transparent hover:bg-white/10"
                }`}
              >
                <Icon className={`w-4 h-4 mx-auto mb-0.5 ${active ? "text-[#D4AF37]" : "text-white/40"}`} />
                <p className="text-white text-[11px] font-medium leading-tight">{preset.name}</p>
              </button>
            );
          })}
        </div>

        {/* Custom Outfit */}
        <button
          data-testid="outfit-custom-toggle"
          onClick={() => { setShowCustom(!showCustom); if (!showCustom) setSelectedOutfit(null); }}
          className={`w-full p-2.5 rounded-xl text-left transition-all flex items-center gap-3 mb-3 ${
            showCustom ? "bg-[#D4AF37]/15 border-2 border-[#D4AF37]" : "bg-white/5 border-2 border-transparent hover:bg-white/10"
          }`}
        >
          <Shirt className={`w-5 h-5 ${showCustom ? "text-[#D4AF37]" : "text-white/40"}`} />
          <div className="flex-1">
            <p className="text-white font-medium text-sm">Custom Outfit</p>
            <p className="text-white/40 text-[11px]">Describe any outfit you want</p>
          </div>
          <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${showCustom ? "rotate-180" : ""}`} />
        </button>

        <AnimatePresence>
          {showCustom && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden mb-3">
              <textarea
                data-testid="outfit-custom-prompt"
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value)}
                placeholder="e.g., A red silk dress with gold jewelry..."
                className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/40 focus:border-[#D4AF37] focus:outline-none text-xs resize-none h-14"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Error */}
        {error && (
          <div className="mb-2 px-3 py-2 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-xs" data-testid="outfit-error">
            {error}
          </div>
        )}

        {/* Generate Button */}
        <button
          data-testid="outfit-generate-btn"
          onClick={handleGenerate}
          disabled={!isReady}
          className={`w-full font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 text-sm ${
            isReady
              ? "bg-[#D4AF37] hover:bg-[#c9a432] text-black shadow-lg shadow-[#D4AF37]/20"
              : "bg-white/5 border border-white/10 text-white/25 cursor-not-allowed"
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Change Outfit (1 Credit)
        </button>
      </div>

      {/* Credit Upsell Modal */}
      <AnimatePresence>
        {showUpsell && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
            onClick={() => setShowUpsell(false)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#111] border border-white/10 rounded-2xl p-6 max-w-sm w-full text-center"
              onClick={e => e.stopPropagation()}>
              <Crown className="w-10 h-10 text-[#D4AF37] mx-auto mb-3" />
              <h3 className="text-lg font-bold text-white mb-2">Need More Credits</h3>
              <p className="text-white/60 text-sm mb-4">
                Current balance: <span className="text-[#D4AF37] font-bold">{user?.credits || 0}</span>
              </p>
              <button onClick={() => { setShowUpsell(false); onNavigate("/pricing"); }}
                className="w-full bg-[#D4AF37] text-black font-bold py-3 rounded-xl hover:bg-[#c9a432] transition-colors"
                data-testid="outfit-upsell-buy-btn">
                Get Credits
              </button>
              <button onClick={() => setShowUpsell(false)}
                className="w-full mt-2 text-white/40 text-sm py-2 hover:text-white/60 transition-colors">
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default OutfitChangePage;
